import { useAtomValue } from 'jotai';
import React, { useMemo, useTransition } from 'react';

import { ConditionalRender, Skeleton } from '@crossnokaye/ux-common';

import { SummaryConfig } from '@repo/sdk/models/components';

import { Animatic } from '@/features/equipment-browser/animatics/animatic';
import { MisconfiguredAnimaticWrapper } from '@/features/equipment-browser/animatics/misconfigured-animatic-wrapper';
import { AnimaticShelf } from '@/features/equipment-browser/animatics/shelf/animatic-shelf';
import { EquipmentDetailsActions } from '@/features/equipment-browser/equipment-details/actions/equipment-details-actions';
import { useSelectEquipment } from '@/features/equipment-browser/equipment-details/hooks/use-select-equipment';
import { equipmentPoolAtom } from '@/features/equipment-browser/equipment-details/state/selected-equipment.state';

import { ActionSidebarTemplate } from './action-sidebar-template';

interface EquipmentDetailsViewProps {
  equipment?: SummaryConfig;
  isLoading?: boolean;
}

export const EquipmentDetailsSidebar = ({
  equipment,
  isLoading,
}: EquipmentDetailsViewProps): React.ReactNode => {
  const equipmentPool = useAtomValue(equipmentPoolAtom);
  const { selectEquipmentAction } = useSelectEquipment();
  const [, startTransition] = useTransition();

  const selectedIndex = useMemo(() => {
    if (!equipment) return 0;
    const index = equipmentPool.findIndex((e) => e.referenceId === equipment.referenceId);
    return index >= 0 ? index : 0;
  }, [equipment, equipmentPool]);

  const animaticItems = useMemo(
    () =>
      equipmentPool.map((eq) => (
        <MisconfiguredAnimaticWrapper
          key={eq.referenceId}
          equipment={eq}
          className="w-[18.75rem] h-[18.75rem] @2xl:w-[15.625rem] @2xl:h-[15.625rem] border-[0.3125rem] border-foreground rounded-md flex items-center justify-center"
        >
          <Animatic equipment={eq} />
        </MisconfiguredAnimaticWrapper>
      )),
    [equipmentPool]
  );

  const handleIndexChange = (newIndex: number) => {
    const newEquipment = equipmentPool[newIndex];
    if (newEquipment && newEquipment.referenceId !== equipment?.referenceId) {
      startTransition(() => {
        selectEquipmentAction(newEquipment.referenceId);
      });
    }
  };

  return (
    <ConditionalRender condition={!isLoading} fallback={<Skeletons />}>
      <ActionSidebarTemplate
        animaticItems={animaticItems}
        selectedIndex={selectedIndex}
        onIndexChange={handleIndexChange}
        shelf={<AnimaticShelf animaticShelf={equipment?.animatic.shelf} />}
        actions={<EquipmentDetailsActions />}
      />
    </ConditionalRender>
  );
};

const Skeletons = () => {
  return (
    <ActionSidebarTemplate
      animaticItems={[
        <Skeleton
          key="skeleton"
          className="w-[18.75rem] h-[18.75rem] @2xl:w-[15.625rem] @2xl:h-[15.625rem]"
        />,
        <Skeleton
          key="skeleton"
          className="w-[18.75rem] h-[18.75rem] @2xl:w-[15.625rem] @2xl:h-[15.625rem]"
        />,
        <Skeleton
          key="skeleton"
          className="w-[18.75rem] h-[18.75rem] @2xl:w-[15.625rem] @2xl:h-[15.625rem]"
        />,
      ]}
      selectedIndex={0}
      onIndexChange={() => {}}
      shelf={<Skeleton className="w-full h-10" />}
      actions={
        <>
          <Skeleton className="w-full h-10" />
          <Skeleton className="w-full h-10" />
        </>
      }
    />
  );
};
